import React, { useState } from "react";
import axios from "axios";
import { Table, Button, Dropdown, Form } from "react-bootstrap";

import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import * as XLSX from "xlsx"; // Import XLSX for exporting data
import { useAuth } from "../../auth/AuthContext";
import {
  fetchPrayerRequests,
  updatePrayerReqStatus,
} from "../../services/PrayerServices";

const API_URL = process.env.REACT_APP_API_URL;

const ListPrayerRequestTablePage  = () => {
  const { user } = useAuth();
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [loading, setLoading] = useState(false); // Loading initially set to false
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(""); // State for from date filter
  const [toDate, setToDate] = useState(""); // State for to date filter
  const [status, setStatus] = useState(""); // State for status filter
  const [actionedBy, setActionedBy] = useState(""); // State for actionedBy filter (text input)

  // Fetch prayer requests with optional filters
  const fetchPrayerRequestsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {}; // Construct the query parameters based on filters

      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (status) params.status = status;
      if (actionedBy) params.actionedBy = actionedBy; // Include actionedBy filter (text)

     // console.log("fetchPrayerRequests params", params);
      const data = await fetchPrayerRequests(params);
      setPrayerRequests(data);
    } catch (err) {
      console.error("Error fetching prayer requests", err);
      setError("Error fetching prayer requests");
    } finally {
      setLoading(false);
    }
  };

  // Handle status change
  const handleChangeStatus = async (id, newStatus) => {
    try {
      let actionedBy = user?.userID || "Admin"; // Update as needed
      await updatePrayerReqStatus(id, newStatus, actionedBy);
      setPrayerRequests(
        prayerRequests.map((prayer) =>
          prayer.id === id
            ? { ...prayer, status: newStatus, actionedBy }
            : prayer
        )
      );
    } catch (err) {
      console.error("Error updating prayer request status", err);
      alert("Error updating prayer request status");
    }
  };

  // Export the prayer request data to XLS
  const handleExportToXLS = () => {
    const ws = XLSX.utils.json_to_sheet(prayerRequests);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prayer Requests");
    XLSX.writeFile(wb, "PrayerRequests.xlsx");
  };

  return (
    <React.Fragment>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>
      {/* Title Section */}
      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">Prayer Requests</h1>
          </div>
        </div>
      </div>

      <div className="container mt-3 mb-3">
        <Form className="mb-4">
          <div className="row g-3">
            {/* From Date Filter */}
            <div className="col-md-2 col-12">
              <Form.Group controlId="fromDate">
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  title="From Date"
                />
              </Form.Group>
            </div>

            {/* To Date Filter */}
            <div className="col-md-2 col-12">
              <Form.Group controlId="toDate">
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  title="To Date"
                />
              </Form.Group>
            </div>

            {/* Status Filter */}
            <div className="col-md-2 col-12">
              <Form.Group controlId="status">
                <Form.Select
                  as="select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  title="Status"
                >
                  <option value="">All</option>
                  <option value="New">New</option>
                  <option value="Pending">Pending</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Sent to Marian">Sent to Marian</option>
                </Form.Select>
              </Form.Group>
            </div>

            {/* Actioned By Filter */}
            <div className="col-md-2 col-12">
              <Form.Group controlId="actionedBy">
                <Form.Control
                  type="text"
                  value={actionedBy}
                  onChange={(e) => setActionedBy(e.target.value)} // Text input for actionedBy
                  placeholder="Enter Actioned by ID"
                  title="Actioned By"
                />
              </Form.Group>
            </div>

            {/* Buttons Section */}
            <div className="col-md-2 col-12 d-flex justify-content-end align-items-center">
              <Button
                className="btn btn-primary btn-sm me-2"
                onClick={fetchPrayerRequestsData}
              >
                <i className="fas fa-list"></i> List
              </Button>
              <Button
                onClick={handleExportToXLS}
                className="btn btn-success btn-sm"
                disabled={prayerRequests.length === 0} // Disable if no data to export
              >
                <i className="fas fa-file-excel"></i> Excel
              </Button>
            </div>
          </div>
        </Form>

        {/* Data Loading */}
        {loading && <div className="alert alert-info">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Prayer Requests Table */}
        {!loading && prayerRequests.length > 0 && (
          <div className="table-responsive mt-3">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="large-column">Message</th>
                  <th>Status</th>
                  <th>Actioned By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {prayerRequests.map((prayer) => (
                  <tr key={prayer.id}>
                    <td>
                      {prayer.firstName} {prayer.lastName}
                    </td>
                    <td>{prayer.email}</td>
                    <td>{prayer.phone || "N/A"}</td>
                    <td className="large-column" title={prayer.message}>
                      {prayer.message}
                    </td>
                    <td>{prayer.status}</td>
                    <td>{prayer.actionedBy || "N/A"}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="success" id="dropdown-basic">
                          Review
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleChangeStatus(prayer.id, "Pending")
                            }
                          >
                            Pending
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleChangeStatus(prayer.id, "Reviewed")
                            }
                          >
                            Reviewed
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleChangeStatus(prayer.id, "Sent to Marian")
                            }
                          >
                            Sent to Marian
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* No Results Message */}
        {!loading && prayerRequests.length === 0 && (
          <p>No prayer requests found for the selected filters.</p>
        )}
      </div>

      <FooterPanel />
    </React.Fragment>
  );
};

export default ListPrayerRequestTablePage ;
